import React, { useState, useEffect, useRef } from 'react';
import { storageManager } from '../utils/storage';
import { PromptItem, Category } from '../utils/types';
import { extractVariables, replaceVariables } from '../utils/variableParser';
import { getCategories } from '../utils/categoryUtils';
import { DEFAULT_CATEGORY_ID } from '../utils/constants';

interface PromptSelectorProps {
  onClose: () => void;
  onSelect: (prompt: PromptItem, processedContent?: string) => void;
}

const PromptSelector: React.FC<PromptSelectorProps> = ({ onClose, onSelect }) => {
  const [prompts, setPrompts] = useState<PromptItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesMap, setCategoriesMap] = useState<Record<string, Category>>({});
  const [filteredPrompts, setFilteredPrompts] = useState<PromptItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptItem | null>(null);
  const [variables, setVariables] = useState<string[]>([]);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [showVariableInput, setShowVariableInput] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isKeyboardNav, setIsKeyboardNav] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [promptsData, categoriesData] = await Promise.all([
          storageManager.getPrompts(),
          getCategories(),
        ]);
        
        const enabledCategories = categoriesData.filter(c => c.enabled);
        const enabledCategoryIds = new Set(enabledCategories.map(c => c.id));
        
        // 过滤提示词：只显示启用的提示词，且属于启用的分类（默认分类总是显示）
        const enabledPrompts = promptsData.filter(p => 
          p.enabled !== false && (enabledCategoryIds.has(p.categoryId) || p.categoryId === DEFAULT_CATEGORY_ID)
        );
        
        setPrompts(enabledPrompts);
        setCategories(enabledCategories);
        
        // 创建分类映射表（只包含启用的分类）
        const categoryMap: Record<string, Category> = {};
        enabledCategories.forEach(cat => {
          categoryMap[cat.id] = cat;
        });
        
        // 确保默认分类在映射表中存在（以防被意外停用）
        const defaultCategory = categoriesData.find(c => c.id === DEFAULT_CATEGORY_ID);
        if (defaultCategory && !categoryMap[DEFAULT_CATEGORY_ID]) {
          categoryMap[DEFAULT_CATEGORY_ID] = defaultCategory;
        }
        
        setCategoriesMap(categoryMap);
      } catch (error) {
        console.error('加载数据失败:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // 过滤提示词
  useEffect(() => {
    let filtered = prompts.filter(prompt => {
      // 分类筛选
      if (selectedCategoryId && prompt.categoryId !== selectedCategoryId) {
        return false;
      }
      
      // 搜索词筛选
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        return (
          prompt.title.toLowerCase().includes(term) ||
          prompt.content.toLowerCase().includes(term) ||
          prompt.tags?.some((tag) => tag.toLowerCase().includes(term))
        );
      }
      
      return true;
    });

    // 排序：置顶的在前面，同级别内按最后修改时间降序
    filtered = filtered.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      
      const aTime = a.lastModified ? new Date(a.lastModified).getTime() : 0;
      const bTime = b.lastModified ? new Date(b.lastModified).getTime() : 0;
      return bTime - aTime;
    });

    setFilteredPrompts(filtered);
    setSelectedIndex(0);
  }, [prompts, searchTerm, selectedCategoryId]);

  // 自动聚焦搜索框
  useEffect(() => {
    if (searchInputRef.current && !showVariableInput) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showVariableInput]);

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();

      if (showVariableInput) return;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp":
          setIsKeyboardNav(true);
          e.preventDefault();
          setSelectedIndex((prev) => 
            e.key === "ArrowDown"
              ? prev === filteredPrompts.length - 1 ? 0 : prev + 1
              : prev === 0 ? filteredPrompts.length - 1 : prev - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (filteredPrompts[selectedIndex]) {
            handlePromptSelect(filteredPrompts[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          if (showVariableInput) {
            setShowVariableInput(false);
            setSelectedPrompt(null);
          } else {
            onClose();
          }
          break;
        case "Tab":
          e.preventDefault();
          cycleCategorySelection(e.shiftKey ? 'prev' : 'next');
          break;
        case "c":
          if ((e.ctrlKey || e.metaKey) && filteredPrompts[selectedIndex]) {
            e.preventDefault();
            copyPrompt(filteredPrompts[selectedIndex]);
          }
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [selectedIndex, filteredPrompts, showVariableInput, categories, selectedCategoryId]);

  // 确保选中项在视图中
  useEffect(() => {
    const selectedElement = document.getElementById(`prompt-item-${selectedIndex}`);
    if (selectedElement && listRef.current) {
      selectedElement.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // 鼠标移动事件
  useEffect(() => {
    const handleMouseMove = () => {
      setIsKeyboardNav(false);
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    return () => document.removeEventListener('mousemove', handleMouseMove, true);
  }, []);

  // 循环切换分类
  const cycleCategorySelection = (direction: 'next' | 'prev') => {
    const allOptions = [null, ...categories.map(cat => cat.id)];
    const currentIndex = allOptions.indexOf(selectedCategoryId);
    
    let nextIndex;
    if (direction === 'next') {
      nextIndex = currentIndex === allOptions.length - 1 ? 0 : currentIndex + 1;
    } else {
      nextIndex = currentIndex === 0 ? allOptions.length - 1 : currentIndex - 1;
    }
    
    setSelectedCategoryId(allOptions[nextIndex]);
  };

  // 复制提示词
  const copyPrompt = async (prompt: PromptItem) => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedId(prompt.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 处理提示词选择
  const handlePromptSelect = (prompt: PromptItem) => {
    const promptVariables = extractVariables(prompt.content);
    
    if (promptVariables.length > 0) {
      setSelectedPrompt(prompt);
      setVariables(promptVariables);
      setVariableValues({});
      setShowVariableInput(true);
    } else {
      onSelect(prompt, prompt.content);
    }
  };

  // 处理变量输入完成
  const handleVariableSubmit = () => {
    if (selectedPrompt) {
      const processedContent = replaceVariables(selectedPrompt.content, variableValues);
      onSelect(selectedPrompt, processedContent);
    }
  };

  // 点击背景关闭
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (showVariableInput) {
        setShowVariableInput(false);
        setSelectedPrompt(null);
      } else {
        onClose();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600 dark:text-gray-300">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={handleBackgroundClick}
    >
      {showVariableInput ? (
        // 变量输入弹窗
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-2xl max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            填写变量 - {selectedPrompt?.title}
          </h3>
          
          <div className="space-y-4">
            {variables.map((variable) => (
              <div key={variable}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {variable}
                </label>
                <input
                  type="text"
                  value={variableValues[variable] || ''}
                  onChange={(e) => setVariableValues(prev => ({
                    ...prev,
                    [variable]: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`请输入 ${variable}`}
                />
              </div>
            ))}
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowVariableInput(false);
                setSelectedPrompt(null);
              }}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            >
              取消
            </button>
            <button
              onClick={handleVariableSubmit}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              确定
            </button>
          </div>
        </div>
      ) : (
        // 主选择器界面
        <div 
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col"
          style={{
            animation: 'fadeInScale 0.25s ease-out',
          }}
        >
          {/* 头部 */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <input
                ref={searchInputRef}
                type="text"
                className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="搜索提示词..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                value={selectedCategoryId || ""}
                onChange={(e) => setSelectedCategoryId(e.target.value || null)}
                className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">所有分类</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 内容区域 */}
          <div
            ref={listRef}
            className="flex-1 overflow-auto p-4"
            style={{ maxHeight: 'calc(80vh - 180px)' }}
          >
            {filteredPrompts.length > 0 ? (
              <div className="space-y-2">
                {filteredPrompts.map((prompt, index) => {
                  const category = categoriesMap[prompt.categoryId];
                  return (
                    <div
                      id={`prompt-item-${index}`}
                      key={prompt.id}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        index === selectedIndex
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-200 dark:border-blue-700'
                          : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                      }`}
                      onClick={() => handlePromptSelect(prompt)}
                      onMouseEnter={() => !isKeyboardNav && setSelectedIndex(index)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {prompt.title}
                          {prompt.pinned && (
                            <span className="ml-2 text-amber-500">📌</span>
                          )}
                        </h3>
                        <button
                          className={`p-1.5 rounded-lg transition-colors ${
                            copiedId === prompt.id
                              ? 'bg-green-100 text-green-600 dark:bg-green-900/50 dark:text-green-400'
                              : 'hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyPrompt(prompt);
                          }}
                          title="复制内容"
                        >
                          {copiedId === prompt.id ? (
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-3">
                        {prompt.content}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                          {category && (
                            <div className="flex items-center gap-1">
                              <div 
                                className="w-2 h-2 rounded-full" 
                                style={{ backgroundColor: category.color || '#6366f1' }}
                              />
                              <span className="text-gray-500 dark:text-gray-400">
                                {category.name}
                              </span>
                            </div>
                          )}
                          {prompt.tags && prompt.tags.length > 0 && (
                            <div className="flex items-center gap-1">
                              {prompt.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                              {prompt.tags.length > 3 && (
                                <span className="text-gray-400 dark:text-gray-500">
                                  +{prompt.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  {searchTerm || selectedCategoryId ? '没有找到匹配的提示词' : '暂无可用的提示词'}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm text-center mt-2">
                  {searchTerm && selectedCategoryId 
                    ? '尝试更改搜索词或分类筛选'
                    : searchTerm 
                    ? '尝试其他关键词'
                    : selectedCategoryId
                    ? '该分类下暂无提示词'
                    : '请先添加一些提示词'
                  }
                </p>
              </div>
            )}
          </div>

          {/* 底部 */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <span>共 {filteredPrompts.length} 个提示词</span>
              <span>Ctrl+C 复制 • ↑↓ 导航 • Tab 切换分类 • Enter 选择</span>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.99);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default PromptSelector;
